import React, { useState, useEffect, useRef } from 'react';
import { purchaseActions } from '../../core/action/purchaseActions';
import Swal from 'sweetalert2';
import AddPurchases from '../../core/modals/purchases/addpurchases';
import PurchaseViewModal from '../../core/modals/purchases/purchaseview';
import { 
    Eye, 
    Edit, 
    Trash2, 
    PlusCircle,
    Download,
    Printer,
    Filter,
    Search,
    ArrowUp,
    ArrowDown,
    ChevronLeft,
    ChevronRight
} from 'feather-icons-react/build/IconComponents';

const PurchaseList = () => {
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        status: '',
        payment_status: '',
        supplier_id: 0,
        date_from: '',
        date_to: ''
    });
    
    // Pagination state from API response
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });
    
    // Modal state
    const [modalType, setModalType] = useState(null); // 'add', 'edit', 'view'
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    
    // Payment history modal state
    const [showPaymentHistory, setShowPaymentHistory] = useState(false);
    const [selectedPurchasePayments, setSelectedPurchasePayments] = useState(null);
    const [paymentHistoryData, setPaymentHistoryData] = useState([]);
    const [paymentHistoryLoading, setPaymentHistoryLoading] = useState(false);
    
    // Sort by most recent first (purchase_date descending)
    const [sortConfig, setSortConfig] = useState({ 
        key: 'purchase_date', 
        direction: 'desc' // Default: latest first
    });

    // Refs to track initial render and prevent unnecessary fetches
    const isInitialMount = useRef(true);
    const prevPageRef = useRef(1);

    // Main fetch function
    const fetchPurchases = async (page = pagination.page) => {
        try {
            setLoading(true);
            
            // Prepare API parameters
            const params = {
                page: page,
                limit: pagination.limit,
                sort_by: sortConfig.key === 'supplier_name' ? 'supplier_name' : sortConfig.key,
                sort_order: sortConfig.direction.toUpperCase(),
                ...(searchTerm && { search: searchTerm }),
                ...(filters.status && { status: filters.status }),
                ...(filters.payment_status && { payment_status: filters.payment_status }),
                ...(filters.supplier_id > 0 && { supplier_id: filters.supplier_id }),
                ...(filters.date_from && { date_from: filters.date_from }),
                ...(filters.date_to && { date_to: filters.date_to })
            };
            
            console.log('Fetching purchases with params:', params);
            
            const result = await purchaseActions.getPurchases(params);
            
            if (result.success) {
                console.log('API Response data:', result.data);
                console.log('Total purchases:', result.total);
                console.log('Current page:', page);
                
                // Set purchases from API response
                setPurchases(result.data || []);
                
                // Update pagination from API response
                if (result.total !== undefined) {
                    setPagination(prev => ({
                        ...prev,
                        page: result.page || page,
                        total: result.total,
                        totalPages: result.totalPages || Math.ceil(result.total / pagination.limit)
                    }));
                }
                
                // Store current page for comparison
                prevPageRef.current = page;
            } else {
                Swal.fire('Error', result.message || 'Failed to load purchases', 'error');
                setPurchases([]);
                setPagination(prev => ({ ...prev, total: 0, totalPages: 1 }));
            }
        } catch (error) {
            console.error('Error fetching purchases:', error);
            Swal.fire('Error', 'Failed to load purchases', 'error');
            setPurchases([]);
            setPagination(prev => ({ ...prev, total: 0, totalPages: 1 }));
        } finally {
            setLoading(false);
        }
    };

    // Initial fetch on mount
    useEffect(() => {
        fetchPurchases(1);
        isInitialMount.current = false;
    }, []);

    // Fetch purchases when page changes
    useEffect(() => {
        if (isInitialMount.current || prevPageRef.current === pagination.page) {
            return;
        }
        
        console.log('Page changed to:', pagination.page);
        fetchPurchases(pagination.page);
    }, [pagination.page]);

    // Fetch purchases when filters or sort change (with debounce for search)
    useEffect(() => {
        if (isInitialMount.current) {
            return;
        }
        
        const timer = setTimeout(() => {
            console.log('Filters/sort/search changed, resetting to page 1');
            setPagination(prev => ({ ...prev, page: 1 }));
            
            setTimeout(() => {
                fetchPurchases(1, true);
            }, 0);
        }, 300);
        
        return () => clearTimeout(timer);
    }, [sortConfig, searchTerm, filters.status, filters.payment_status, 
        filters.supplier_id, filters.date_from, filters.date_to]);

    // Handle sort
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Clean up any leftover modal backdrops
    const cleanupModalBackdrops = () => {
        const backdrops = document.querySelectorAll('.modal-backdrop');
        backdrops.forEach(backdrop => backdrop.remove());
        document.body.classList.remove('modal-open');
        document.body.style.paddingRight = '';
        document.body.style.overflow = '';
    };

    // Safe method to show Bootstrap modal
    const showBootstrapModal = () => {
        cleanupModalBackdrops();
        const modalElement = document.getElementById('add-units');
        if (modalElement) {
            if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
                window.$(modalElement).modal('show');
            } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
                let modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                if (!modalInstance) {
                    modalInstance = new window.bootstrap.Modal(modalElement);
                }
                modalInstance.show();
            } else {
                modalElement.classList.add('show');
                modalElement.style.display = 'block';
                const backdrop = document.createElement('div');
                backdrop.className = 'modal-backdrop fade show';
                document.body.appendChild(backdrop);
                document.body.classList.add('modal-open');
            }
        }
    };

    // Safe method to hide Bootstrap modal
    const hideBootstrapModal = () => {
        const modalElement = document.getElementById('add-units');
        if (modalElement) {
            if (typeof window.$ !== 'undefined' && window.$.fn.modal) {
                window.$(modalElement).modal('hide');
                setTimeout(cleanupModalBackdrops, 300);
            } else if (typeof window.bootstrap !== 'undefined' && window.bootstrap.Modal) {
                const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
                if (modalInstance) {
                    modalInstance.hide();
                    setTimeout(cleanupModalBackdrops, 300);
                }
            } else {
                modalElement.classList.remove('show');
                modalElement.style.display = 'none';
                cleanupModalBackdrops();
            }
        }
    };

    // Handle modal close
    const handleModalClose = () => {
        hideBootstrapModal();
        setModalType(null);
        setSelectedPurchase(null);
    };

    // Handle add purchase
    const handleAddPurchase = () => {
        setModalType('add');
        setSelectedPurchase(null);
        setTimeout(() => showBootstrapModal(), 50);
    };

    // Handle edit
    const handleEdit = (purchase) => {
        setModalType('edit');
        setSelectedPurchase(purchase);
        setTimeout(() => showBootstrapModal(), 50);
    };

    // Handle view
    const handleView = (purchase) => {
        setSelectedPurchase(purchase);
        setShowViewModal(true);
    };

    // Handle view payment history
    const handleViewPayments = async (purchase) => {
        try {
            setPaymentHistoryLoading(true);
            setSelectedPurchasePayments(purchase);
            
            const result = await purchaseActions.getPaymentHistory(purchase.id, 1, 100);
            
            if (result.success) {
                setPaymentHistoryData(result.data.payments || []);
                setShowPaymentHistory(true);
            } else {
                Swal.fire('Error', 'Failed to load payment history', 'error');
            }
        } catch (error) {
            console.error('Error fetching payment history:', error);
            Swal.fire('Error', 'Failed to load payment history', 'error');
        } finally {
            setPaymentHistoryLoading(false);
        }
    };

    // Handle delete
    const handleDelete = async (id) => {
        Swal.fire({
            title: 'Delete Purchase?',
            text: 'Are you sure you want to delete this purchase? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const deleteResult = await purchaseActions.deletePurchase(id);
                    if (deleteResult.success) {
                        Swal.fire('Deleted!', 'Purchase has been deleted.', 'success');
                        fetchPurchases(pagination.page, true);
                    } else {
                        Swal.fire('Error', deleteResult.message || 'Failed to delete purchase', 'error');
                    }
                } catch (error) {
                    console.error('Error deleting purchase:', error);
                    Swal.fire('Error', 'Failed to delete purchase', 'error');
                }
            }
        });
    };

    // Handle page change
    const handlePageChange = (pageNumber) => {
        console.log('Changing page to:', pageNumber);
        if (pageNumber !== pagination.page) {
            setPagination(prev => ({ ...prev, page: pageNumber }));
        }
    };

    // Handle next page
    const handleNextPage = () => {
        if (pagination.page < pagination.totalPages) {
            console.log('Going to next page:', pagination.page + 1);
            setPagination(prev => ({ ...prev, page: prev.page + 1 }));
        }
    };

    // Handle previous page
    const handlePrevPage = () => {
        if (pagination.page > 1) {
            console.log('Going to previous page:', pagination.page - 1);
            setPagination(prev => ({ ...prev, page: prev.page - 1 }));
        }
    };

    // Calculate statistics
    const calculateStats = () => {
        const totalPurchases = pagination.total;
        
        const totalAmount = purchases.reduce((sum, p) => sum + parseFloat(p.grand_total || 0), 0);
        const paidAmount = purchases.reduce((sum, p) => sum + parseFloat(p.paid_amount || 0), 0);
        const dueAmount = purchases.reduce((sum, p) => sum + parseFloat(p.due_amount || 0), 0);
        
        const pendingPurchases = purchases.filter(p => p.status === 'pending').length;
        const receivedPurchases = purchases.filter(p => p.status === 'received').length;

        return {
            totalPurchases,
            totalAmount: totalAmount.toFixed(2),
            paidAmount: paidAmount.toFixed(2),
            dueAmount: dueAmount.toFixed(2),
            pendingPurchases,
            receivedPurchases
        };
    };

    const stats = calculateStats();

    // Handle search input with debouncing
    const handleSearchInput = (e) => {
        setSearchTerm(e.target.value);
    };

    // Handle search submit
    const handleSearch = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Handle Enter key in search
    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // Reset filters
    const resetFilters = () => {
        setSearchTerm('');
        setFilters({
            status: '',
            payment_status: '',
            supplier_id: 0,
            date_from: '',
            date_to: ''
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Handle filter change
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({ ...prev, [filterName]: value }));
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (e) {
            return dateString;
        }
    };

    // Format date with time
    // const formatDateTime = (dateString) => {
    //     if (!dateString) return 'N/A';
    //     try {
    //         const date = new Date(dateString);
    //         return date.toLocaleDateString('en-IN', {
    //             day: '2-digit',
    //             month: '2-digit',
    //             year: 'numeric',
    //             hour: '2-digit',
    //             minute: '2-digit'
    //         });
    //     } catch (e) {
    //         return dateString;
    //     }
    // };

    // Format currency
    const formatCurrency = (amount) => {
        const num = parseFloat(amount || 0);
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(num);
    };

    // Get status badge class
    const getStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case 'pending': return 'badge bg-warning';
            case 'approved': return 'badge bg-info';
            case 'received': return 'badge bg-success';
            case 'returned': return 'badge bg-info';
            case 'canceled': return 'badge bg-danger';
            case 'completed': return 'badge bg-success';
            default: return 'badge bg-secondary';
        }
    };

    // Get payment status badge class
    const getPaymentStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case 'unpaid': return 'badge bg-danger';
            case 'partial': return 'badge bg-warning';
            case 'paid': return 'badge bg-success';
            default: return 'badge bg-secondary';
        }
    };

    // Handle success (after add/edit)
    const handleSuccess = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchPurchases(1, true);
        handleModalClose();
    };

    // Clean up on component unmount
    useEffect(() => {
        return () => {
            cleanupModalBackdrops();
        };
    }, []);

    // Calculate current page items indices
    const indexOfFirstItem = purchases.length > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0;
    const indexOfLastItem = purchases.length > 0 ? Math.min(pagination.page * pagination.limit, pagination.total) : 0;

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Purchase Management</h4>
                        <h6>Manage your purchase orders</h6>
                    </div>
                    <ul className="table-top-head">
                        <li>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleAddPurchase}
                            >
                                <PlusCircle className="me-2" size={18} />
                                Add New Purchase
                            </button>
                        </li>
                        <li>
                            <button className="btn btn-outline-primary">
                                <Download className="me-2" size={18} />
                                Export
                            </button>
                        </li>
                        <li>
                            <button className="btn btn-outline-secondary">
                                <Printer className="me-2" size={18} />
                                Print
                            </button>
                        </li>
                    </ul>
                </div>

                {/* Statistics Cards */}
                <div className="row mb-4">
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Total Purchases</h5>
                                <h3 className="card-text text-primary">{pagination.total}</h3>
                                <p className="card-text text-muted">All time</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Total Amount</h5>
                                <h3 className="card-text text-success">₹{stats.totalAmount}</h3>
                                <p className="card-text text-muted">Grand total</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Paid Amount</h5>
                                <h3 className="card-text text-info">₹{stats.paidAmount}</h3>
                                <p className="card-text text-muted">Amount received</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Due Amount</h5>
                                <h3 className="card-text text-danger">₹{stats.dueAmount}</h3>
                                <p className="card-text text-muted">Pending payment</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Pending</h5>
                                <h3 className="card-text text-warning">{stats.pendingPurchases}</h3>
                                <p className="card-text text-muted">Pending purchases</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-2 col-sm-4 col-6">
                        <div className="card">
                            <div className="card-body">
                                <h5 className="card-title">Received</h5>
                                <h3 className="card-text text-success">{stats.receivedPurchases}</h3>
                                <p className="card-text text-muted">Received purchases</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search and Filters */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row">
                            <div className="col-lg-4 col-md-6 mb-3">
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <Search size={18} />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search by reference, supplier, notes..."
                                        value={searchTerm}
                                        onChange={handleSearchInput}
                                        onKeyPress={handleKeyPress}
                                    />
                                    <button
                                        className="btn btn-outline-primary"
                                        onClick={handleSearch}
                                    >
                                        Search
                                    </button>
                                </div>
                            </div>
                            <div className="col-lg-2 col-md-3 col-sm-6 mb-3">
                                <select
                                    className="form-select"
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                >
                                    <option value="">All Status</option>
                                    <option value="pending">Pending</option>
                                    <option value="approved">Approved</option>
                                    <option value="received">Received</option>
                                    <option value="canceled">Canceled</option>
                                    <option value="returned">Returned</option>
                                </select>
                            </div>
                            <div className="col-lg-2 col-md-3 col-sm-6 mb-3">
                                <select
                                    className="form-select"
                                    value={filters.payment_status}
                                    onChange={(e) => handleFilterChange('payment_status', e.target.value)}
                                >
                                    <option value="">All Payment</option>
                                    <option value="unpaid">Unpaid</option>
                                    <option value="partial">Partial</option>
                                    <option value="paid">Paid</option>
                                </select>
                            </div>
                            <div className="col-lg-2 col-md-3 col-sm-6 mb-3">
                                <input
                                    type="date"
                                    className="form-control"
                                    value={filters.date_from}
                                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                                    placeholder="From Date"
                                />
                            </div>
                            <div className="col-lg-2 col-md-3 col-sm-6 mb-3">
                                <input
                                    type="date"
                                    className="form-control"
                                    value={filters.date_to}
                                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                                    placeholder="To Date"
                                />
                            </div>
                            <div className="col-lg-2 col-md-3 col-sm-6 mb-3 d-flex align-items-end">
                                <button
                                    className="btn btn-outline-secondary w-100"
                                    onClick={resetFilters}
                                >
                                    <Filter className="me-2" size={18} />
                                    Reset
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Purchases Table */}
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center p-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3">Loading purchases...</p>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th onClick={() => handleSort('reference_no')} style={{ cursor: 'pointer' }}>
                                                    Reference No
                                                    {sortConfig.key === 'reference_no' && (
                                                        sortConfig.direction === 'asc' ? 
                                                        <ArrowUp className="ms-1" size={14} /> : 
                                                        <ArrowDown className="ms-1" size={14} />
                                                    )}
                                                </th>
                                                <th onClick={() => handleSort('supplier_name')} style={{ cursor: 'pointer' }}>
                                                    Supplier
                                                    {sortConfig.key === 'supplier_name' && (
                                                        sortConfig.direction === 'asc' ? 
                                                        <ArrowUp className="ms-1" size={14} /> : 
                                                        <ArrowDown className="ms-1" size={14} />
                                                    )}
                                                </th>
                                                <th onClick={() => handleSort('purchase_date')} style={{ cursor: 'pointer' }}>
                                                    Date
                                                    {sortConfig.key === 'purchase_date' && (
                                                        sortConfig.direction === 'asc' ? 
                                                        <ArrowUp className="ms-1" size={14} /> : 
                                                        <ArrowDown className="ms-1" size={14} />
                                                    )}
                                                </th>
                                                <th onClick={() => handleSort('grand_total')} style={{ cursor: 'pointer' }}>
                                                    Amount
                                                    {sortConfig.key === 'grand_total' && (
                                                        sortConfig.direction === 'asc' ? 
                                                        <ArrowUp className="ms-1" size={14} /> : 
                                                        <ArrowDown className="ms-1" size={14} />
                                                    )}
                                                </th>
                                                <th>Status</th>
                                                <th>Payment</th>
                                                <th>Payment Info</th>
                                                <th onClick={() => handleSort('due_amount')} style={{ cursor: 'pointer' }}>
                                                    Due Amount
                                                    {sortConfig.key === 'due_amount' && (
                                                        sortConfig.direction === 'asc' ? 
                                                        <ArrowUp className="ms-1" size={14} /> : 
                                                        <ArrowDown className="ms-1" size={14} />
                                                    )}
                                                </th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {purchases.length === 0 ? (
                                                <tr>
                                                    <td colSpan="9" className="text-center py-5">
                                                        <div className="text-muted">
                                                            <h5>No purchases found</h5>
                                                            <p>Try adjusting your search or filters</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                purchases.map((purchase) => (
                                                    <tr key={purchase.id}>
                                                        <td>
                                                            <strong>{purchase.reference_no}</strong>
                                                            <br />
                                                            <small className="text-muted">
                                                                Created: {formatDate(purchase.created_at)}
                                                            </small>
                                                            {purchase.notes && (
                                                                <small className="d-block text-muted">
                                                                    {purchase.notes.length > 30 ? 
                                                                        `${purchase.notes.substring(0, 30)}...` : 
                                                                        purchase.notes}
                                                                </small>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {purchase.supplier_name || `Supplier #${purchase.supplier_id}`}
                                                        </td>
                                                        <td>{formatDate(purchase.purchase_date)}</td>
                                                        <td>
                                                            <strong>{formatCurrency(purchase.grand_total)}</strong>
                                                            <small className="d-block text-muted">
                                                                Paid: {formatCurrency(purchase.paid_amount)}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <span className={getStatusBadge(purchase.status)}>
                                                                {purchase.status?.toUpperCase()}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={getPaymentStatusBadge(purchase.payment_status)}>
                                                                {purchase.payment_status ? purchase.payment_status.toUpperCase() : 'N/A'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="payment-info">
                                                                {purchase.payment_count > 0 ? (
                                                                    <>
                                                                        <span className="badge bg-info me-1" title="Number of payments">
                                                                            {purchase.payment_count} payment{purchase.payment_count > 1 ? 's' : ''}
                                                                        </span>
                                                                        {purchase.last_payment_date && (
                                                                            <small className="d-block text-muted">
                                                                                Last: {formatDate(purchase.last_payment_date)}
                                                                                {purchase.last_payment_method && (
                                                                                    <> via <span className="text-capitalize">{purchase.last_payment_method}</span></>
                                                                                )}
                                                                            </small>
                                                                        )}
                                                                        <button
                                                                            className="btn btn-link btn-sm p-0 mt-1"
                                                                            onClick={() => handleViewPayments(purchase)}
                                                                            title="View all payments"
                                                                        >
                                                                            <small>View all</small>
                                                                        </button>
                                                                    </>
                                                                ) : (
                                                                    <span className="text-muted">No payments</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className={purchase.due_amount > 0 ? 'text-danger' : 'text-success'}>
                                                                {formatCurrency(purchase.due_amount)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="btn-group" role="group">
                                                                <button
                                                                    className="btn btn-sm btn-outline-info"
                                                                    onClick={() => handleView(purchase)}
                                                                    title="View Details"
                                                                >
                                                                    <Eye size={16} />
                                                                </button>
                                                                <button
                                                                    className="btn btn-sm btn-outline-primary"
                                                                    onClick={() => handleEdit(purchase)}
                                                                    title="Edit Purchase"
                                                                >
                                                                    <Edit size={16} />
                                                                </button>
                                                                <button
                                                                    className="btn btn-sm btn-outline-danger"
                                                                    onClick={() => handleDelete(purchase.id)}
                                                                    title="Delete Purchase"
                                                                >
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {pagination.total > 0 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div>
                                            Showing <strong>{indexOfFirstItem}-{indexOfLastItem}</strong> of{' '}
                                            <strong>{pagination.total}</strong> purchases
                                            <span className="text-muted ms-2">(Page {pagination.page} of {pagination.totalPages})</span>
                                        </div>
                                        <nav>
                                            <ul className="pagination mb-0">
                                                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                    <button 
                                                        className="page-link" 
                                                        onClick={handlePrevPage}
                                                        disabled={pagination.page === 1}
                                                    >
                                                        <ChevronLeft size={16} />
                                                    </button>
                                                </li>
                                                
                                                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                                    let pageNumber;
                                                    if (pagination.totalPages <= 5) {
                                                        pageNumber = i + 1;
                                                    } else if (pagination.page <= 3) {
                                                        pageNumber = i + 1;
                                                    } else if (pagination.page >= pagination.totalPages - 2) {
                                                        pageNumber = pagination.totalPages - 4 + i;
                                                    } else {
                                                        pageNumber = pagination.page - 2 + i;
                                                    }
                                                    
                                                    return (
                                                        <li 
                                                            key={pageNumber} 
                                                            className={`page-item ${pagination.page === pageNumber ? 'active' : ''}`}
                                                        >
                                                            <button 
                                                                className="page-link" 
                                                                onClick={() => handlePageChange(pageNumber)}
                                                            >
                                                                {pageNumber}
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                                
                                                <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                                    <button 
                                                        className="page-link" 
                                                        onClick={handleNextPage}
                                                        disabled={pagination.page === pagination.totalPages}
                                                    >
                                                        <ChevronRight size={16} />
                                                    </button>
                                                </li>
                                            </ul>
                                        </nav>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Payment History Modal */}
                {showPaymentHistory && (
                    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} id="payment-history-modal">
                        <div className="modal-dialog modal-lg modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        Payment History - {selectedPurchasePayments?.reference_no}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setShowPaymentHistory(false)}
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    {paymentHistoryLoading ? (
                                        <div className="text-center p-4">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <p className="mt-2">Loading payment history...</p>
                                        </div>
                                    ) : (
                                        <div className="payment-history-modal">
                                            {paymentHistoryData.length > 0 && (
                                                <div className="row mb-3">
                                                    <div className="col-md-4">
                                                        <div className="card bg-light">
                                                            <div className="card-body py-2">
                                                                <small className="text-muted">Total Payments</small>
                                                                <h6 className="mb-0">{paymentHistoryData.length}</h6>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <div className="card bg-light">
                                                            <div className="card-body py-2">
                                                                <small className="text-muted">Total Amount</small>
                                                                <h6 className="mb-0 text-success">
                                                                    ₹{paymentHistoryData.reduce((sum, p) => sum + p.amount, 0).toFixed(4)}
                                                                </h6>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <div className="card bg-light">
                                                            <div className="card-body py-2">
                                                                <small className="text-muted">Last Payment</small>
                                                                <h6 className="mb-0">
                                                                    {paymentHistoryData[0]?.date 
                                                                        ? formatDate(paymentHistoryData[0].date)
                                                                        : 'N/A'}
                                                                </h6>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="table-responsive">
                                                <table className="table table-hover table-sm">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th>#</th>
                                                            <th>Date</th>
                                                            <th>Amount</th>
                                                            <th>Method</th>
                                                            <th>Reference</th>
                                                            <th>Notes</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {paymentHistoryData.length === 0 ? (
                                                            <tr>
                                                                <td colSpan="6" className="text-center py-4">
                                                                    No payment records found
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            paymentHistoryData.map((payment, index) => (
                                                                <tr key={payment.id || index}>
                                                                    <td>{index + 1}</td>
                                                                    <td>{formatDate(payment.date)}</td>
                                                                    <td className="text-success fw-bold">₹{payment.amount.toFixed(4)}</td>
                                                                    <td>
                                                                        <span className={`badge ${
                                                                            payment.method === 'cash' ? 'bg-success' :
                                                                            payment.method === 'bank_transfer' ? 'bg-info' :
                                                                            payment.method === 'cheque' ? 'bg-warning' :
                                                                            payment.method === 'credit_card' ? 'bg-primary' : 'bg-secondary'
                                                                        }`}>
                                                                            {payment.method}
                                                                        </span>
                                                                    </td>
                                                                    <td>{payment.reference || <span className="text-muted">-</span>}</td>
                                                                    <td>{payment.notes || <span className="text-muted">-</span>}</td>
                                                                </tr>
                                                            ))
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer">
                                    <button 
                                        className="btn btn-secondary" 
                                        onClick={() => setShowPaymentHistory(false)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal backdrop for payment history modal */}
                {showPaymentHistory && <div className="modal-backdrop fade show"></div>}

                {/* Add/Edit Purchase Modal */}
                <AddPurchases
                    editData={modalType === 'edit' ? selectedPurchase : null}
                    onSuccess={handleSuccess}
                    onClose={handleModalClose}
                    show={modalType === 'add' || modalType === 'edit'}
                />

                {/* Purchase View Modal */}
                {showViewModal && (
                    <PurchaseViewModal
                        purchase={selectedPurchase}
                        onClose={() => setShowViewModal(false)}
                    />
                )}
            </div>
        </div>
    );
};

export default PurchaseList;